import React, { useEffect, useState } from 'react';
import { Polyline, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useStore } from '../../../../../store';
import { useMapContext } from '../../context/map-context';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const LonLatAxes = () => {
  const {
    configurationValues, dimensions, rangePerDegree,
  } = useMapContext();
  const map = useMap();
  const { mapOrigin, mapImage } = useStore();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [lines, setLines] = useState<any>(null);
  const originLat = Number(mapOrigin.data.longitude);
  const originLng = Number(mapOrigin.data.latitude);

  useEffect(() => {
    if (!dimensions?.height || !dimensions?.width) return;
    const { xofffset, yoffset, pixelsPerLength } = mapImage.data;

    const x = Number(dimensions?.width) - Number(xofffset);
    const y = Number(dimensions?.height) - Number(yoffset);

    const xLengthInFt1 = (1 / Number(pixelsPerLength)) * Number(xofffset); // x1
    const yLengthInFt1 = (1 / Number(pixelsPerLength)) * Number(yoffset); // y1

    const xLengthInFt2 = (1 / Number(pixelsPerLength)) * Number(x); // x2
    const yLengthInFt2 = (1 / Number(pixelsPerLength)) * Number(y); // y2
    // becouse in backend lat and lon are mixed
    const rangePerDegreeLat = rangePerDegree.lon;
    const rangePerDegreeLon = rangePerDegree.lat;

    const latOffset = xLengthInFt1 / rangePerDegreeLat;
    const lonOffset = yLengthInFt1 / rangePerDegreeLon;

    const latOffset2 = xLengthInFt2 / rangePerDegreeLat;
    const lonOffset2 = yLengthInFt2 / rangePerDegreeLon;

    const topLeft: [number, number] = [originLat - latOffset, originLng + lonOffset2];
    const topRight: [number, number] = [originLat + latOffset2, originLng + lonOffset2];
    const bottomLeft: [number, number] = [originLat - latOffset, originLng - lonOffset];
    const bottomRight: [number, number] = [originLat + latOffset2, originLng - lonOffset];
    type Coordinates = [number, number];

    function findBounds(points: Coordinates[]): { lat: { max: number; min: number };
      lng: { max: number; min: number } } {
      const longitudes = points.map((point) => point[0]);
      const latitudes = points.map((point) => point[1]);

      return {
        lat: {
          max: Math.max(...longitudes),
          min: Math.min(...longitudes),
        },
        lng: {
          max: Math.max(...latitudes),
          min: Math.min(...latitudes),
        },
      };
    }

    const bounds = findBounds([topLeft, topRight, bottomLeft, bottomRight]);

    const updateLines = () => {
      const latLine = [
        [bounds.lng.min, bounds.lat.min],
        [bounds.lng.max, bounds.lat.min],
      ];

      const lngLine = [
        [bounds.lng.min, bounds.lat.min],
        [bounds.lng.min, bounds.lat.max],
      ];

      const latMainLine = [
        [originLng, bounds.lat.min],
        [originLng, bounds.lat.max],
      ];

      const lngMainLine = [
        [bounds.lng.max, originLat],
        [bounds.lng.min, originLat],
      ];

      const generatePerpendicularMarks = (start: number[],
        end: number[], isLatLine: boolean) => {
        const interval = 0.005; // coord interval
        const length = 0.000025; // mark length
        const marks = [];
        const [startLat, startLng] = start;
        const steps = Math.floor((isLatLine ? end[0] - startLat : end[1] - startLng) / interval);
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i <= steps; i++) {
          const lat = isLatLine ? startLat + i * interval : startLat;
          const lng = isLatLine ? startLng : startLng + i * interval;

          // eslint-disable-next-line no-continue
          if (lat === originLat && lng === originLng) continue;
          const gridMark = isLatLine
            ? [
              [lat, lng],
              [lat, lngLine[1][1]],
            ]
            : [
              [lat, lng],
              [latLine[1][0], lng],
            ];

          const mark = isLatLine
            ? [
              [lat, originLat - length],
              [lat, originLat + length],
            ]
            : [
              [originLng - length, lng],
              [originLng + length, lng],
            ];

          marks.push({
            mark: configurationValues?.visibility?.llGrid ? [...gridMark]
              : [...mark],
            lat,
            lng,
          });
        }
        return marks;
      };

      const latMarks = generatePerpendicularMarks(latLine[0], latLine[1], true);
      const lngMarks = generatePerpendicularMarks(lngLine[0], lngLine[1], false);

      setLines({
        latMainLine,
        lngMainLine,
        latMarks,
        lngMarks,
        east: [originLng, bounds.lat.max + 0.001],
        north: [bounds.lng.max + 0.001, originLat],
      });
    };

    map.on('move', updateLines);
    updateLines();

    // eslint-disable-next-line consistent-return
    return () => {
      map.off('move', updateLines);
    };
  }, [map, mapOrigin, configurationValues?.visibility?.llGrid, dimensions, mapImage]);

  if (!lines) return null;

  const createCustomIcon = (text:string, isMainLine:boolean) => L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="border-radius: 4px; color: ${isMainLine ? '#00ACAC' : ' #3800EE'}; font-size: 12px; font-weight:500">${text}</div>`,
  });

  return (

    <>
      {configurationValues?.visibility?.llAxes
      && (
        <>
          <Polyline positions={lines.latMainLine} pathOptions={{ color: '#00ACAC', weight: 2 }} />
          <Polyline positions={lines.lngMainLine} pathOptions={{ color: '#00ACAC', weight: 2 }} />
        </>
      )}

      { (configurationValues?.visibility?.llAxes || configurationValues?.visibility?.llGrid)
      && lines.latMarks.map((mark) => (
        <Polyline key={mark.lat} positions={mark.mark} pathOptions={{ color: configurationValues?.visibility?.llGrid ? '#3800EE' : '#00ACAC', weight: 2 }} />
      ))}

      { (configurationValues?.visibility?.llAxes || configurationValues?.visibility?.llGrid)
      && lines.lngMarks.map((mark) => (
        <Polyline key={mark.lat} positions={mark.mark} pathOptions={{ color: configurationValues?.visibility?.llGrid ? '#3800EE' : '#00ACAC', weight: 2 }} />
      ))}

      {configurationValues?.visibility?.llAxesLabels && (
        <Marker
          position={lines.east}
          icon={createCustomIcon('East', true)}
        />
      )}
      {configurationValues?.visibility?.llAxesLabels && (
        <Marker
          position={lines.north}
          icon={createCustomIcon('North', true)}
        />
      )}

      { configurationValues?.visibility?.llAxesLabels && lines.latMarks.map((mark) => (
        <Marker
          key={mark.lat}
          position={configurationValues?.visibility?.llGrid
            ? [mark.lat, mark.lng - 0.0003] : [mark.lat + 0.00001, originLat + 0.0001]}
          icon={createCustomIcon(`${mark.lat.toFixed(4)}`, !configurationValues?.visibility?.llGrid)}
        />
      ))}

      { configurationValues?.visibility?.llAxesLabels && lines.lngMarks.map((mark) => (
        <Marker
          key={mark.lng}
          position={configurationValues?.visibility?.llGrid
            ? [mark.lat - 0.0001, mark.lng - 0.0001] : [originLng - 0.0001, mark.lng - 0.0001]}
          icon={createCustomIcon(`${mark.lng.toFixed(4)}`, !configurationValues?.visibility?.llGrid)}
        />
      ))}
    </>

  );
};
